import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Check, ChevronsUpDown, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { logActivity } from "@/lib/activityLog";
import {
  AccreditationTerm,
  JournalistOption,
  PassType,
  TERM_DESCRIPTIONS,
  TERM_LABELS,
  defaultValidToForTerm,
} from "./types";

const TERM_TO_PASS_TYPE: Record<AccreditationTerm, PassType> = {
  short_term: "day",
  medium_term: "session",
  long_term: "annual",
};

const schema = z.object({
  journalist_id: z.string().min(1, "Select a journalist"),
  accreditation_term: z.enum(["short_term", "medium_term", "long_term"]),
  valid_from: z.string().min(1, "Required"),
  valid_to: z.string().min(1, "Required"),
});

type FormValues = z.infer<typeof schema>;

interface PassIssueSheetProps {
  open: boolean;
  onClose: () => void;
  onIssued: () => void;
  journalists: JournalistOption[];
  orgId: string;
  preselectedJournalistId?: string;
}

export function PassIssueSheet({
  open,
  onClose,
  onIssued,
  journalists,
  orgId,
  preselectedJournalistId,
}: PassIssueSheetProps) {
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [jPopoverOpen, setJPopoverOpen] = useState(false);

  const { register, handleSubmit, watch, setValue, formState: { errors } } =
    useForm<FormValues>({
      resolver: zodResolver(schema),
      defaultValues: {
        journalist_id: preselectedJournalistId ?? "",
        accreditation_term: "long_term",
        valid_from: new Date().toISOString().split("T")[0],
        valid_to: defaultValidToForTerm("long_term", new Date().toISOString().split("T")[0]),
      },
    });

  const term = watch("accreditation_term") as AccreditationTerm;
  const validFrom = watch("valid_from");
  const selectedJournalistId = watch("journalist_id");

  const selectedJournalist = journalists.find((j) => j.id === selectedJournalistId);

  function handleTermChange(t: AccreditationTerm) {
    setValue("accreditation_term", t);
    if (validFrom) {
      setValue("valid_to", defaultValidToForTerm(t, validFrom));
    }
  }

  async function onSubmit(values: FormValues) {
    setSubmitting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { error } = await supabase.from("press_passes" as any).insert({
        org_id: orgId,
        journalist_id: values.journalist_id,
        pass_number: "", // trigger auto-sets
        pass_type: TERM_TO_PASS_TYPE[values.accreditation_term],
        accreditation_term: values.accreditation_term,
        valid_from: values.valid_from,
        valid_to: values.valid_to,
        status: "active",
        issued_by: user?.id ?? null,
      });
      if (error) throw error;
      const journalist = journalists.find((j) => j.id === values.journalist_id);
      await logActivity({
        orgId,
        actionType: "press_pass_issued",
        description: `Press pass issued to ${journalist ? `${journalist.first_name} ${journalist.last_name}` : "a journalist"}`,
        metadata: { journalist_id: values.journalist_id, accreditation_term: values.accreditation_term },
      });
      toast({ title: "Press pass issued successfully" });
      onIssued();
      onClose();
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-[480px] overflow-y-auto">
        <SheetHeader className="mb-6">
          <SheetTitle>Issue New Press Pass</SheetTitle>
        </SheetHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Journalist selector */}
          <div className="space-y-1.5">
            <Label>Journalist</Label>
            <Popover open={jPopoverOpen} onOpenChange={setJPopoverOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  className="w-full justify-between font-normal"
                >
                  {selectedJournalist
                    ? `${selectedJournalist.first_name} ${selectedJournalist.last_name}${selectedJournalist.outlet_name ? `, ${selectedJournalist.outlet_name}` : ""}`
                    : "Select journalist…"}
                  <ChevronsUpDown className="ml-2 w-4 h-4 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[420px] p-0">
                <Command>
                  <CommandInput placeholder="Search journalist…" />
                  <CommandList>
                    <CommandEmpty>No journalist found.</CommandEmpty>
                    <CommandGroup>
                      {journalists.map((j) => (
                        <CommandItem
                          key={j.id}
                          value={`${j.first_name} ${j.last_name} ${j.outlet_name ?? ""}`}
                          onSelect={() => {
                            setValue("journalist_id", j.id);
                            setJPopoverOpen(false);
                          }}
                        >
                          <Check
                            className={cn(
                              "mr-2 w-4 h-4",
                              selectedJournalistId === j.id ? "opacity-100" : "opacity-0"
                            )}
                          />
                          <div>
                            <div className="font-medium">
                              {j.first_name} {j.last_name}
                            </div>
                            {j.outlet_name && (
                              <div className="text-xs text-muted-foreground">
                                {j.outlet_name}
                              </div>
                            )}
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            {errors.journalist_id && (
              <p className="text-justify text-xs text-destructive">{errors.journalist_id.message}</p>
            )}
          </div>

          {/* Accreditation term */}
          <div className="space-y-1.5">
            <Label>Accreditation Term</Label>
            <RadioGroup
              value={term}
              onValueChange={(v) => handleTermChange(v as AccreditationTerm)}
              className="flex flex-col gap-3"
            >
              {(["short_term", "medium_term", "long_term"] as AccreditationTerm[]).map((t) => (
                <div key={t} className="flex items-start gap-2">
                  <RadioGroupItem value={t} id={`term-${t}`} className="mt-0.5" />
                  <Label htmlFor={`term-${t}`} className="cursor-pointer font-normal">
                    <div className="font-medium">{TERM_LABELS[t]}</div>
                    <div className="text-xs text-muted-foreground">{TERM_DESCRIPTIONS[t]}</div>
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>

          {/* Valid from */}
          <div className="space-y-1.5">
            <Label htmlFor="valid_from">Valid From</Label>
            <Input
              id="valid_from"
              type="date"
              {...register("valid_from")}
              onChange={(e) => {
                setValue("valid_from", e.target.value);
                if (e.target.value) {
                  setValue("valid_to", defaultValidToForTerm(term, e.target.value));
                }
              }}
            />
            {errors.valid_from && (
              <p className="text-justify text-xs text-destructive">{errors.valid_from.message}</p>
            )}
          </div>

          {/* Valid to */}
          <div className="space-y-1.5">
            <Label htmlFor="valid_to">Valid To</Label>
            <Input
              id="valid_to"
              type="date"
              {...register("valid_to")}
            />
            <p className="text-justify text-xs text-muted-foreground">
              Prefilled from the selected term — adjust if needed.
            </p>
            {errors.valid_to && (
              <p className="text-justify text-xs text-destructive">{errors.valid_to.message}</p>
            )}
          </div>

          <div className="pt-4 border-t flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              style={{ backgroundColor: "#006B3C" }}
              className="text-white hover:opacity-90"
            >
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Issue Pass
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}
